import React, { useState } from 'react';
import Modal from '../common/Modal';
import { Customer, ContactPerson } from '../../types/customer';
import { useLanguage } from '../../contexts/LanguageContext';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  UserIcon,
  StarIcon as StarIconSolid,
  ClipboardDocumentIcon
} from '@heroicons/react/24/solid';
import { StarIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';

interface SendInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer;
  onSuccess?: () => void;
}

const SendInvitationModal: React.FC<SendInvitationModalProps> = ({
  isOpen,
  onClose,
  customer,
  onSuccess
}) => {
  const { t } = useLanguage();
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  const generateToken = () => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  };

  const handleToggleContact = (contactId: string) => {
    setSelectedContactIds(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    const availableContacts = customer.contactPersons?.filter(cp => !cp.hasUserAccount || !cp.userId) || [];
    setSelectedContactIds(availableContacts.map(cp => cp.id!));
  };

  const handleDeselectAll = () => {
    setSelectedContactIds([]);
  };

  const handleCopyLink = async (contact: ContactPerson) => {
    try {
      // First, get fresh data from database
      const customerDoc = await getDoc(doc(db, 'customers', customer.id));
      if (!customerDoc.exists()) {
        throw new Error('Customer not found');
      }
      
      const freshData = customerDoc.data();
      const freshContact = freshData.contactPersons?.find((cp: any) => cp.id === contact.id);
      
      let token = freshContact?.registrationToken;
      
      if (!token) {
        // Generate new token
        token = generateToken();
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 7);

        const updatedContactPersons = freshData.contactPersons?.map((cp: any) =>
          cp.id === contact.id
            ? { ...cp, invitationSentAt: new Date(), registrationToken: token, registrationTokenExpiry: expiryDate }
            : cp
        );

        await updateDoc(doc(db, 'customers', customer.id), {
          contactPersons: updatedContactPersons,
          updatedAt: serverTimestamp()
        });
      }

      const link = `${window.location.origin}/customer-signup/${token}`;
      await navigator.clipboard.writeText(link);
      toast.success(`Link vágólapra másolva: ${contact.name}`);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Hiba történt a link másolása során');
    }
  };

  const handleSendInvitations = async () => {
    if (selectedContactIds.length === 0) {
      toast.error('Válassz ki legalább egy kapcsolattartót!');
      return;
    }

    setIsSending(true);

    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7); // 7 days expiry

      const updatedContactPersons = customer.contactPersons?.map(cp => {
        if (selectedContactIds.includes(cp.id!)) {
          const token = generateToken();
          return {
            ...cp,
            invitationSentAt: new Date(),
            registrationToken: token,
            registrationTokenExpiry: expiryDate
          };
        }
        return cp;
      });

      await updateDoc(doc(db, 'customers', customer.id), {
        contactPersons: updatedContactPersons,
        updatedAt: serverTimestamp()
      });

      // Generate signup links and copy to clipboard
      const links = selectedContactIds.map(contactId => {
        const contact = updatedContactPersons?.find(cp => cp.id === contactId);
        if (contact?.registrationToken) {
          return `${contact.name}: ${window.location.origin}/customer-signup/${contact.registrationToken}`;
        }
        return '';
      }).filter(Boolean);

      await navigator.clipboard.writeText(links.join('\n\n'));

      toast.success(`${selectedContactIds.length} meghívó elküldve! Linkek vágólapra másolva.`);
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
      setSelectedContactIds([]);
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error('Hiba történt a meghívók küldése során');
    } finally {
      setIsSending(false);
    }
  };

  const availableContacts = customer.contactPersons?.filter(cp => !cp.hasUserAccount || !cp.userId) || [];
  const registeredContacts = customer.contactPersons?.filter(cp => cp.hasUserAccount && cp.userId) || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Meghívó küldése"
      size="lg"
    >
      <div className="p-6 space-y-6">
        {/* Info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Kattints a "Link másolása" gombra a regisztrációs link vágólapra másolásához. A linket elküldheted emailben vagy más csatornán.
          </p>
        </div>

        {/* Available Contacts */}
        {customer.contactPersons && customer.contactPersons.length > 0 ? (
          availableContacts.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Elérhető kapcsolattartók ({availableContacts.length})
              </h3>

              <div className="space-y-2">
                {availableContacts.map(contact => (
                  <div
                    key={contact.id}
                    className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {contact.name}
                        </span>
                        {contact.isPrimary && (
                          <StarIconSolid className="h-4 w-4 text-yellow-500" title="Elsődleges kapcsolattartó" />
                        )}
                        {contact.invitationSentAt && (
                          <span className="text-xs text-yellow-600 dark:text-yellow-400">
                            (Függőben)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {contact.email}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {contact.position}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyLink(contact)}
                      className="ml-3 inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 rounded-md transition-colors"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                      Link másolása
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Minden kapcsolattartó már rendelkezik fiókkal!
              </p>
            </div>
          )
        ) : (
          <div className="text-center py-8">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Még nincs kapcsolattartó hozzáadva
            </p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Először adj hozzá kapcsolattartókat az ügyfél szerkesztése során.
            </p>
          </div>
        )}

        {/* Registered Contacts (info only) */}
        {registeredContacts.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              Már regisztrált kapcsolattartók ({registeredContacts.length})
            </h3>
            <div className="space-y-2">
              {registeredContacts.map(contact => (
                <div
                  key={contact.id}
                  className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                >
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {contact.name}
                      </span>
                      {contact.isPrimary && (
                        <StarIconSolid className="h-4 w-4 text-yellow-500" />
                      )}
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {contact.email}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
          >
            Bezárás
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SendInvitationModal;
